<template>
  <div class="page-wrapper">
    <div class="stepper-header">
      <v-img alt="Step Logo" class="step5-image step-logo" />
      <div class="reg-titre1" v-html="title"></div>
    </div>

    <div v-if="requiresVerification">
      <v-form ref="USER" lazy-validation @submit.prevent.stop="next">
        <div>
          <div v-if="verification">
            <div class="email-text" v-html="textVerif" />

            <edit-field ref="CODE"
                        :value="code"
                        style="width: 100px; padding-bottom: 16px;"
                        class="custom-fieldS3"
                        :over-ride-placeholder="overRidePlaceholder"
                        :hide-label="true"
                        :edit-field-info="codeFieldInfo"
                        :required="true"
                        :context-id="synchro98.id"
                        :data-table="true"
                        @updated="setCode" />

            <div class="codeNonRecuContainer">
              <span class="info">Code valable 5 minutes. </span>
              <span class="reset-code" @click="resetIdent">
                {{ delayReset ? resetTxt + ' ' + delayLabel : resetTxt }}
              </span>
            </div>
          </div>
        </div>
      </v-form>
    </div>
    <div v-else>
      <div class="email-text" v-html="textAuthentified" />
    </div>
  </div>

  <div class="action-footer onboarding-authent-page__footer">
    <loader-button :label="precedentBtnTxt"
                   :outlined="true"
                   @callback="previous" />
    <loader-button :label="suivantBtnTxt"
                   :loading="loading"
                   :disabled="!userLoaded"
                   @callback="next" />
  </div>
</template>

<script lang="ts">
  import { defineComponent, type PropType } from "vue";
  import CrudUser from '@/generated/cruds/CrudUser.ts';
  import { Sections } from '@/generated/classes/AppSchema.ts';
  import EditField from "@/framework/base/components/EditField.vue";
  import { EditFieldTheme, EditFieldType, FieldInfo } from "@/framework/base/components/FieldInfo.ts";
  import Ressource from "@/framework/ressource/classes/Ressource.ts";
  import {
    AppButtons,
    AppCustoms,
    FwkSections,
    RessourceAppButton,
    RessourceAppCustom,
    RessourceFwkSection
  } from "@/ressources/globalRessources.ts";
  import User from "@/generated/classes/User.ts";
  import { Alerts } from "@/framework/base/helpers/Alerts.ts";
  import { storeSession } from "@/generated/stores";
  import Synchro98 from "../../generated/classes/Synchro98";

  export default defineComponent({
    components: {
      EditField
    },
    extends: CrudUser,
    props: {
      synchro98: {
        type: Synchro98,
        required: true
      }
    },
    data() {
      return {
        currentStep: 1,
        sectionName: Sections.USER,
        delayReset: false,
        delay: 0,
        delayLabel: '',
        delayRemaining: 0,
        overRidePlaceholder: '___-___',
        verification: true,
        loading: false,
        userId: 0
      }
    },
    computed: {
      userLoaded(): boolean {
        if (this.synchro98.id > 0) {
          if (this.userId == 0) {
            this.userId = this.synchro98.id;
            this.currentStep = this.firstVerificationStep;
            this.sendCurrentCode();
          }
          return true;
        }
        return false;
      },
      mailVerified(): boolean {
        return (this.synchro98.identMailId || 0) > 0;
      },
      numeroVerified(): boolean {
        return (this.synchro98.identNumeroId || 0) > 0;
      },
      shouldVerifyMail(): boolean {
        return !this.mailVerified;
      },
      shouldVerifyNumero(): boolean {
        return !this.numeroVerified;
      },
      requiresVerification(): boolean {
        return this.shouldVerifyMail || this.shouldVerifyNumero;
      },
      firstVerificationStep(): number {
        return this.shouldVerifyMail ? 1 : 2;
      },
      codeFieldInfo(): FieldInfo {
        const ressource = new Ressource();
        const type = EditFieldType.STRING;
        const precision = 2;
        const format = null;
        const mask = this.mask;
        const focus = null;
        const live = null;
        return new FieldInfo(ressource, type, precision, mask, format, focus, live, EditFieldTheme.OUTLINED);
      },
      resetTxt(): string {
        return RessourceFwkSection(FwkSections.CODE_NOT_RECEIVED).label;
      },
      textVerif() {
        if (this.currentStep == 1) {
          return RessourceAppCustom(AppCustoms.ONBOARDING_VERIF_MAIL).label + '<span class="email"> ' + this.synchro98.mail + '</span>.';
        } else if (this.currentStep == 2) {
          return RessourceAppCustom(AppCustoms.ONBOARDING_VERIF_NUMERO).label + '<span class="email"> ' + this.synchro98.numero + '</span>.';
        }
      },
      textAuthentified() {
        return RessourceAppCustom(AppCustoms.ONBOARDING_AUTHENTIFIED).label.
          replace('$MAIL', '<span class="email" > ' + this.synchro98.mail + ' </span>').
          replace('$NUMERO', '<span class="email" > ' + this.synchro98.numero + ' </span>');

      },
      title(): string {
        return 'Bonjour ' + this.synchro98.prenom + ',<br>' + RessourceAppCustom(AppCustoms.ONBOARDING_ETAPE1_LABEL).label;
      },
      suivantBtnTxt(): string {
        return RessourceAppButton(AppButtons.SUIVANT).label;
      },
      precedentBtnTxt(): string {
        return RessourceAppButton(AppButtons.PRECEDENT).label;
      },
      code(): string {
        return this.synchro98.code;
      },
    },
    methods: {
      setCode(value: string) {
        this.synchro98.code = value;
      },
      mask(value: string): string {
        return 'NNN-NNN';
      },
      resetIdent(): void {
        this.sendCurrentCode();
        this.setDelayReset();
      },
      setDelayReset() {
        this.delayReset = true;
        if (this.delay == 0) {
          this.delay = 2000;
        } else if (this.delay == 2000) {
          this.delay = 15000;
        } else {
          this.delay = this.delay * 2;
        }
        this.delayRemaining = this.delay;
        this.setDelayLabel();
        setTimeout(() => this.clearDelayReset(), 1000);
      },
      clearDelayReset() {
        this.delayRemaining -= 1000;
        if (this.delayRemaining > 0) {
          this.setDelayLabel();
          setTimeout(() => this.clearDelayReset(), 1000);
        } else {
          this.delayReset = false;
        }
      },
      setDelayLabel() {
        this.delayLabel = ' (' + (this.delayRemaining / 1000).toFixed(0) + ' s)';
      },
      validateEmail(): void {
        User.validateMail(this.synchro98).then((res) => {
          if (res) {
            this.synchro98.code = '';
            this.synchro98.identMailId = this.synchro98.mailId;
            if (this.shouldVerifyNumero) {
              this.currentStep = 2;
              User.sendCodeNumero(this.synchro98);
            } else {
              this.$emit('next');
            }
          }
        });
      },
      validateNumero(): void {
        User.validateNumero(this.synchro98).then((res) => {
          if (res) {
            this.synchro98.code = '';
            this.synchro98.identNumeroId = this.synchro98.numeroId;
            this.$emit('next')
          }
        });
      },
      sendCurrentCode(): void {
        if (this.currentStep == 1 && this.shouldVerifyMail) {
          User.sendCodeMail(this.synchro98);
        } else if (this.currentStep == 2 && this.shouldVerifyNumero) {
          User.sendCodeNumero(this.synchro98);
        }
      },
      previous() {
        this.$emit('previous');
      },
      next() {
        if (!this.requiresVerification) {
          this.$emit('next');
        } else if (this.verification && !(this.$refs.CODE as InstanceType<typeof EditField>).touch()) {
          if (this.currentStep == 1) {
            this.validateEmail();
          } else if (this.currentStep == 2) {
            this.validateNumero();
          }
          return;
        }
      }
    }
  })
</script>

<style scoped>

  .intro-text {
    font-size: 16px;
    font-weight: 500;
  }

  .intro {
    color: rgb(var(--v-theme-grey20));
  }

  .name {
    color: rgb(var(--v-theme-primary50));
  }

  .email-text {
    font-size: 14px;
    font-weight: 400;
    color: rgb(var(--v-theme-secondary20));
  }

  .email-text :deep(.email) {
    font-size: 16px;
    font-weight: 400;
    color: rgb(var(--v-theme-information50));
  }

  .codeNonRecuContainer {
    display: flex;
    font-size: 12px;
    font-weight: 400;
    gap: 4px;
    padding-bottom: 16px;
  }

  .info {
    color: rgb(var(--v-theme-secondary50));
  }

  .reset-code {
    color: rgb(var(--v-theme-primary50));
    cursor: pointer;
    transition: color 0.3s, text-decoration 0.3s;
  }

    .reset-code:hover {
      text-decoration: underline;
      color: #c01f1b;
    }

  .onboarding-authent-page__footer {
    gap: 8px;
  }

    .onboarding-authent-page__footer :deep(.v-btn) {
      width: 262px;
    }

  @media (max-width: 600px) {
    .onboarding-authent-page__footer :deep(.v-btn) {
      width: 100%;
    }
  }
</style>
